import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

type Agent = {
  name: string;
  source: "embedded" | "user";
  yaml: string;
  hasEmbeddedDefault: boolean;
  updatedAt: string;
};

export function AgentsSection() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/agents");
      if (!res.ok) throw new Error(`agents: ${res.status}`);
      const data = await res.json();
      setAgents(data.items ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load agents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const current = agents.find((a) => a.name === selected) ?? null;

  const open = (a: Agent) => {
    setSelected(a.name);
    setDraft(a.yaml);
    setErrors([]);
  };

  const validate = async () => {
    if (!current) return;
    const res = await fetch(`/api/agents/${current.name}/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: current.name, yaml: draft }),
    });
    const data = await res.json();
    setErrors(data.valid ? [] : data.errors);
  };

  const save = async () => {
    if (!current) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/agents/${current.name}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: current.name, yaml: draft }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `save failed: ${res.status}`);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "save failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await fetch(`/api/agents/${current.name}/reset`, { method: "POST" });
      await load();
      const refreshed = agents.find((a) => a.name === current.name);
      if (refreshed) setDraft(refreshed.yaml);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading agents…</p>;

  return (
    <div className="grid grid-cols-[260px_1fr] gap-6">
      <div className="flex flex-col gap-1">
        {agents.map((a) => (
          <button
            key={a.name}
            onClick={() => open(a)}
            className={`flex items-center justify-between rounded-md px-3 py-2 text-left text-sm ${
              selected === a.name ? "bg-accent" : "hover:bg-accent/50"
            }`}
          >
            <span>{a.name}</span>
            <Badge variant={a.source === "user" ? "default" : "secondary"}>{a.source}</Badge>
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-3">
        {error && <p className="text-sm text-destructive">{error}</p>}
        {!current && <p className="text-sm text-muted-foreground">Select an agent to edit.</p>}
        {current && (
          <>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={18}
              className="font-mono text-xs"
            />
            {errors.length > 0 && (
              <ul className="list-inside list-disc text-sm text-destructive">
                {errors.map((er, i) => (
                  <li key={i}>{er}</li>
                ))}
              </ul>
            )}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={validate} disabled={busy}>
                Validate
              </Button>
              <Button onClick={save} disabled={busy}>
                Save
              </Button>
              {current.hasEmbeddedDefault && current.source === "user" && (
                <Button variant="ghost" onClick={reset} disabled={busy}>
                  Reset to default
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
